import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useMemo,
} from 'react';
import {
  colorStyle, RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import {
  childrenStyles, headerStyles, iconsStyles, interactiveIconStyles, leftHeaderStyles, mainStyles, rightHeaderStyles,
  titleStyle, titleStyles,
} from '~/_shared/components/toolboxContainer/toolboxContainer.styles';
import { ExportImageMode } from '~/_shared/types/exportImage/exportImage';
import { useMobileTopBarOpenSelector } from '~/store/frontendState/mobileUI/mobileUI.selectors';
import { useMapSettingsExportImageSettingsSelector } from '~/store/mapSettings/toolsState/exportImageSettings/exportImageSettings.selectors';
import { useTheme } from '../../themes/theme.hooks';

export type AdditionalToolboxAction = Readonly<{
  icon: IconProp;
  isActive?: boolean;
  isDisabled?: boolean;
  onClick: () => void;
  tooltipText?: string;
}>;

type ToolboxContainerProps = Readonly<{
  title: string;
  titleIcon?: IconProp;
  onClose?: () => void;
  additionalAction?: AdditionalToolboxAction;
  className?: string;
  onTitleClick?: () => void;
  children?: React.ReactNode;
}>;

export const ToolboxContainerComponent: FC<ToolboxContainerProps> = props => {
  const theme = useTheme();
  const isMobileTopBarOpen = useMobileTopBarOpenSelector();
  const isLegendOverMapMode = useMapSettingsExportImageSettingsSelector()?.mode === ExportImageMode.MapAndLegend;

  const adjustedTitle = useMemo(() =>
    props.title.match(/.*?\/|.+$/g)?.map((line, index) => <span key={index}>{line}</span>), //insures text breaks after "/"
  [props.title]);

  return (
    <div css={mainStyles({ adjustHeightToContent: isLegendOverMapMode })}>
      <div
        css={headerStyles({ theme })}
      >
        <div
          css={leftHeaderStyles}
          onClick={props.onTitleClick}
        >
          {props.titleIcon && (
            <FontAwesomeIcon
              css={iconsStyles}
              icon={props.titleIcon}
            />
          )}
          <LimitNumberOfLinesComponent
            css={titleStyles({ needsIndent: !props.additionalAction })}
            maxNumberOfLines={2}
          >
            <span css={titleStyle} >
              {adjustedTitle}
            </span>
          </LimitNumberOfLinesComponent>
        </div>
        <div css={rightHeaderStyles}>
          {props.additionalAction && (
            <RoundButtonComponent
              buttonStyle={RoundButtonStyle.Custom}
              css={colorStyle({ isSelected: props.additionalAction.isActive, isDisabled: props.additionalAction.isDisabled })}
              icon={props.additionalAction.icon}
              isDisabled={props.additionalAction.isDisabled}
              onClick={props.additionalAction.onClick}
              size={RoundButtonSize.Medium}
              tooltipLabel={props.additionalAction.tooltipText}
            />
          )}
          {props.onClose && (
            <FontAwesomeIcon
              css={interactiveIconStyles}
              icon={faXmark}
              onClick={props.onClose}
              data-testid="close-sidebar"
            />
          )}
        </div>
      </div>

      <div css={childrenStyles({ theme, isSpaceReduced: isMobileTopBarOpen })}>
        {props.children}
      </div>
    </div>
  );
};
